// Kinds understood by the FileModalOwner component. The kind is resolved at the
// call site (where the domain context is known) so the modal itself stays dumb.
export const FileModalOwnerKind = {
  PROSPECT: 'prospect',
  USER: 'user',
  ANONYMOUS_PROSPECT: 'anonymousProspect',
  ANONYMOUS_MODERATOR: 'anonymousModerator',
  SELF: 'self'
}

const createOwner = (kind, payload = {}) => ({
  kind,
  prospect: null,
  user: null,
  anonymousId: null,
  ...payload
})

export const ownerFromProspect = ({ prospect } = {}) => {
  if (!prospect) {
    return null
  }
  return createOwner(FileModalOwnerKind.PROSPECT, { prospect })
}

export const ownerFromUser = ({ user } = {}) => {
  if (!user) {
    return null
  }
  return createOwner(FileModalOwnerKind.USER, { user })
}

// Forum responses and threads share the same author shape (user, prospect,
// anonymousState, anonymousId, isOwnerModerator). Visibility mirrors
// ForumResponseAvatar/ForumResponseProfile: an anonymous post is revealed to its
// own author and hidden (letter / moderator logo) for everybody else.
export const ownerFromForumModel = ({ model, currentUserId = null } = {}) => {
  if (!model) {
    return null
  }
  const isOwn = !!(
    model.prospect &&
    model.prospect.id &&
    currentUserId &&
    model.prospect.id === currentUserId
  )
  const isHidden = !!model.anonymousState && !isOwn

  if (isHidden) {
    return createOwner(
      model.isOwnerModerator ? FileModalOwnerKind.ANONYMOUS_MODERATOR : FileModalOwnerKind.ANONYMOUS_PROSPECT,
      { anonymousId: model.anonymousId || null }
    )
  }

  if (model.prospect) {
    return ownerFromProspect({ prospect: model.prospect })
  }
  if (model.user) {
    return ownerFromUser({ user: model.user })
  }
  return null
}

// Study conversation messages identify the moderator via `isOwnerUser`. The
// study UI hides moderator identity (StudyConversationMessageAvatar/-ProfileName
// render only a masked letter/name), so the modal must not resolve a real user
// here — otherwise the viewer header would leak the moderator's name/photo.
// Participant messages belong to the current participant (the stream prospect),
// so study shows them as "You" instead of their name.
export const ownerFromStudyMessage = ({ message, streamParameters } = {}) => {
  if (!message || message.isOwnerUser) {
    return null
  }
  const prospect = message.prospect || streamParameters?.prospect || null
  if (!prospect) {
    return null
  }
  return createOwner(FileModalOwnerKind.SELF, { prospect })
}
